// Provider-agnostic Web Push (VAPID) helper — shared by coach + client apps.

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export function pushPermission() {
  return typeof Notification !== 'undefined' ? Notification.permission : 'default';
}

// Some browsers (iOS Safari) only allow push when the PWA is installed to the Home Screen.
export function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

export function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

export function isAndroid() {
  return /android/i.test(window.navigator.userAgent);
}

// The client PWA is scoped to /my (see public/manifest.json). Registering the push service worker at the
// SAME /my scope is REQUIRED for Android/Chromium to attribute its notifications to the installed client
// WebAPK (a root '/'-scoped SW is outside the client scope, so Chrome shows them as generic browser
// notifications and cannot place the launcher dot on the STP OS icon). Coaches keep the default root scope.
export const CLIENT_PUSH_SCOPE = '/my';

// Register /push-sw.js. A narrower `scope` (e.g. /my, a sub-path of the script location /) is always
// permitted with no special header. Omit `scope` for the coach (default root '/').
function registerPushSW(scope) {
  return scope
    ? navigator.serviceWorker.register('/push-sw.js', { scope })
    : navigator.serviceWorker.register('/push-sw.js');
}

// Pre-Option-B clients registered the push SW at root '/'. When a CLIENT migrates to the /my scope we must
// stop the server pushing to that OLD root endpoint (otherwise the client gets a duplicate, Chrome-
// attributed push). We only ever return the endpoint so the caller can remove the CLIENT's server-side row
// (a scoped delete). We NEVER unsubscribe it in-browser: on a dual-role device the coach shares that exact
// browser endpoint, and killing it would break coach push. Returns null when there is no separate root sub.
async function oldRootClientEndpoint(scope) {
  if (scope !== CLIENT_PUSH_SCOPE) return null;
  try {
    const rootReg = await navigator.serviceWorker.getRegistration('/');
    if (!rootReg || new URL(rootReg.scope).pathname !== '/') return null;
    const oldSub = await rootReg.pushManager.getSubscription();
    return oldSub ? oldSub.endpoint : null;
  } catch { return null; }
}

// Honest, best-effort snapshot of the ACTUAL push state (never inferred from in-app category prefs):
// web permission, whether a live PushManager subscription exists in THIS context, and how many
// subscriptions the server holds for this account. Note: the Android OS-level notification permission
// (POST_NOTIFICATIONS) is NOT reliably observable from the web layer — callers must say so honestly.
export async function getPushDiagnostics(apiClient, statusPath = '/portal/push/status', { scope } = {}) {
  const out = {
    supported: isPushSupported(), standalone: isStandalone(), android: isAndroid(), ios: isIOS(),
    permission: (typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'),
    hasSubscription: false, serverSubs: null, vapidConfigured: null,
  };
  try {
    if (out.supported) {
      // Check the registration for THIS role's scope (client → /my, coach → root) so hasSubscription
      // reflects the correct registration rather than a lingering root-scoped one.
      const reg = (await navigator.serviceWorker.getRegistration(scope || '/push-sw.js'))
        || (await navigator.serviceWorker.getRegistration());
      const sub = reg && (await reg.pushManager.getSubscription());
      out.hasSubscription = !!sub;
    }
  } catch { /* ignore */ }
  try {
    const { data } = await apiClient.get(statusPath);
    out.serverSubs = typeof data.subscriptions === 'number' ? data.subscriptions : null;
    out.vapidConfigured = data.vapid_configured;
  } catch { /* ignore — server status optional */ }
  return out;
}

// Compares an existing PushSubscription's applicationServerKey to the current server VAPID key.
// A mismatch means the subscription was created against a different/older key and can never receive
// our pushes (the server keeps failing/pruning it) — so it must be refreshed rather than reused.
function subscriptionMatchesKey(sub, keyBytes) {
  try {
    const existing = sub && sub.options && sub.options.applicationServerKey;
    if (!existing) return false; // unknown key → treat as mismatch and re-subscribe to be safe
    const a = new Uint8Array(existing);
    if (a.length !== keyBytes.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== keyBytes[i]) return false;
    return true;
  } catch { return false; }
}

// apiClient: axios instance (coach `api` or `clientApi`); subscribePath: '/push/subscribe' | '/portal/push/subscribe'
// scope: register the SW at a specific scope (client → CLIENT_PUSH_SCOPE '/my'; coach → omit for root '/').
export async function enablePush(apiClient, subscribePath, { scope } = {}) {
  if (!isPushSupported()) throw new Error('unsupported');
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') throw new Error('denied');
  const oldEndpoint = await oldRootClientEndpoint(scope); // captured BEFORE we register the /my SW
  const reg = await registerPushSW(scope);
  await navigator.serviceWorker.ready;
  const { data } = await apiClient.get('/push/vapid-public-key');
  if (!data.public_key) throw new Error('no-key');
  const appServerKey = urlBase64ToUint8Array(data.public_key);

  let sub = await reg.pushManager.getSubscription();
  // Repair a stale subscription created against a different VAPID key (e.g. after a key change or
  // certain redeploys). Reusing it would look "enabled" while every push silently fails and the
  // backend prunes it — causing the enable banner to reappear and no pushes to arrive.
  if (sub && !subscriptionMatchesKey(sub, appServerKey)) {
    try { await sub.unsubscribe(); } catch { /* ignore */ }
    sub = null;
  }
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: appServerKey,
    });
  }
  const json = sub.toJSON();
  await apiClient.post(subscribePath, { endpoint: json.endpoint, keys: json.keys, expirationTime: sub.expirationTime || null });
  // Migration: drop the CLIENT's server-side copy of the old root endpoint so it stops receiving the
  // duplicate, Chrome-attributed push. Scoped delete only — never unsubscribes the shared browser sub.
  if (scope === CLIENT_PUSH_SCOPE && oldEndpoint && oldEndpoint !== json.endpoint) {
    const unsubPath = subscribePath.replace(/\/subscribe$/, '/unsubscribe');
    await apiClient.post(unsubPath, { endpoint: oldEndpoint, keys: {} }).catch(() => {});
  }
  return true;
}

export async function disablePush(apiClient, unsubscribePath, { scope } = {}) {
  const reg = (await navigator.serviceWorker.getRegistration(scope || '/push-sw.js'))
    || (await navigator.serviceWorker.getRegistration());
  const sub = reg && (await reg.pushManager.getSubscription());
  if (sub) {
    await apiClient.post(unsubscribePath, { endpoint: sub.endpoint, keys: sub.toJSON().keys }).catch(() => {});
    await sub.unsubscribe().catch(() => {});
  }
  return true;
}

// Full "start from scratch" reset: force the device to drop its (possibly dead) push subscription,
// clear ALL of this account's stored subscriptions server-side, then create + save a brand-new one.
// Fixes the case where a subscription is key-valid but its endpoint has gone stale (backend reports
// "delivered" yet nothing reaches the device). apiClient must be authenticated.
export async function resetPush(apiClient, { subscribePath, resetPath, scope } = {}) {
  if (!isPushSupported()) throw new Error('unsupported');
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') throw new Error('denied');
  const reg = await registerPushSW(scope);
  await navigator.serviceWorker.ready;

  // 1) Drop the current subscription ON THIS registration so the OS mints a fresh one. For a client on
  //    /my this is the /my registration only — the root/coach registration (a different scope) is never
  //    touched, so a dual-role device's coach push keeps working.
  try {
    const existing = await reg.pushManager.getSubscription();
    if (existing) await existing.unsubscribe();
  } catch { /* ignore */ }

  // 2) Clear every stored subscription for this account server-side (removes stale/dead endpoints,
  //    including any old root-scoped one stored under this client). Scoped to this owner only.
  await apiClient.post(resetPath).catch(() => {});

  // 3) Fresh subscribe against the current VAPID key + persist.
  const { data } = await apiClient.get('/push/vapid-public-key');
  if (!data.public_key) throw new Error('no-key');
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(data.public_key),
  });
  const json = sub.toJSON();
  await apiClient.post(subscribePath, { endpoint: json.endpoint, keys: json.keys, expirationTime: sub.expirationTime || null });
  return true;
}

// Mirror the client bearer token into IndexedDB (shared between the page and the service worker) so the
// SW's `pushsubscriptionchange` handler can re-register a rotated endpoint against /portal/push/subscribe
// even when no page is open — localStorage is not readable from a service worker. Best-effort only.
export async function mirrorClientTokenForSW(token) {
  try {
    if (typeof indexedDB === 'undefined') return;
    const dbi = await new Promise((resolve, reject) => {
      const r = indexedDB.open('stp-sw-auth', 1);
      r.onupgradeneeded = () => { try { r.result.createObjectStore('kv'); } catch { /* exists */ } };
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => reject(r.error);
    });
    await new Promise((resolve, reject) => {
      const tx = dbi.transaction('kv', 'readwrite');
      if (token) tx.objectStore('kv').put(token, 'client_token');
      else tx.objectStore('kv').delete('client_token');
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
  } catch { /* ignore — SW self-heal is best-effort */ }
}
